import { Mail, MessageCircle, MapPin, Instagram, Linkedin, Clock } from "lucide-react";
import ContactForm from "@/components/site/ContactForm";
import SectionHeader from "@/components/site/SectionHeader";
import { BlobA } from "@/components/site/BlobBackground";
import { SITE } from "@/lib/siteConfig";

export default function ContactPage() {
  const waLink = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(SITE.whatsappMessage)}`;
  return (
    <>
      <section className="relative overflow-hidden hero-wash">
        <BlobA className="absolute -top-20 -right-20 w-[400px] h-[400px] hidden sm:block" />
        <div className="container-narrow relative py-16 sm:py-20 lg:py-24">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold" data-testid="contact-eyebrow">Contact</div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-ink" data-testid="contact-title">
              Say hello. We read every message.
            </h1>
            <p className="mt-5 text-ink-muted text-lg leading-[1.75]" data-testid="contact-subtitle">
              Questions about the Foundation Track? Looking to collaborate, partner, or volunteer? Drop us a note —
              we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      <section className="container-narrow py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
          <aside className="lg:col-span-5 space-y-5">
            <InfoCard icon={<Mail className="w-5 h-5" />} title="Email us" sub="For thoughtful, longer conversations.">
              <a href={`mailto:${SITE.email}`} data-testid="contact-info-email" className="text-terracotta hover:underline text-[15px]">{SITE.email}</a>
            </InfoCard>
            <InfoCard icon={<MessageCircle className="w-5 h-5" />} title="WhatsApp" sub="Quickest way to reach us.">
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="contact-info-whatsapp" className="text-terracotta hover:underline text-[15px]">
                Chat on WhatsApp
              </a>
            </InfoCard>
            <InfoCard icon={<MapPin className="w-5 h-5" />} title="Based out of" sub="Fieldwork & in-person sessions.">
              <div className="text-ink text-[15px]">Gurgaon, India</div>
            </InfoCard>
            <InfoCard icon={<Clock className="w-5 h-5" />} title="Response time" sub="We don't outsource our inbox.">
              <div className="text-ink text-[15px]">Within 2–3 working days</div>
            </InfoCard>
            <div className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">Follow along</div>
              <div className="mt-3 flex items-center gap-3">
                <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="contact-instagram-link" className="p-2 rounded-full bg-[rgba(200,106,74,0.10)] text-terracotta hover:bg-[rgba(200,106,74,0.18)] transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href={SITE.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="contact-linkedin-link" className="p-2 rounded-full bg-[rgba(126,155,134,0.18)] text-moss hover:bg-[rgba(126,155,134,0.28)] transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon, title, sub, children }) {
  return (
    <div className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[rgba(200,106,74,0.10)] text-terracotta flex items-center justify-center shrink-0">{icon}</div>
        <div className="min-w-0">
          <div className="font-display text-base text-ink">{title}</div>
          <div className="text-xs text-ink-muted">{sub}</div>
          <div className="mt-2">{children}</div>
        </div>
      </div>
    </div>
  );
}
